/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { z } from 'zod'

import * as CommonTypes from './common-types'

export const ProductStatus = {
	Draft: 'DRAFT',
	Published: 'PUBLISHED',
	Archived: 'ARCHIVED'
} as const
export type ProductStatusType = (typeof ProductStatus)[keyof typeof ProductStatus]
export type ProductImage = {
	uuid: string
	position: number
	isPrimary: boolean
	variantUuid?: string
	asset: CommonTypes.Asset
}
export type ProductListItem = {
	uuid: string
	title: string
	imageUrl?: string
	salesChannels: string[]
	salesChannelGroups: string[]
	pricingMethod: CommonTypes.PricingMethodType[]
	variants: {
		uuid: string
		pricingMethod: CommonTypes.PricingMethodType
	}[]
	status: ProductStatusType
	primaryImage?: ProductImage
}
export const ThirdPartySource = {
	Struct: 'STRUCT',
	LsRetail: 'LS_RETAIL',
	BusinessCentral: 'BUSINESS_CENTRAL'
} as const
export type ThirdPartySourceType = (typeof ThirdPartySource)[keyof typeof ThirdPartySource]
export type Price = {
	salesChannel?: {
		name: string
		uuid: string
	}
	salesChannelGroup?: {
		name: string
		uuid: string
	}
	currencyCode: string
	priceInfo: {
		taxInclusive: boolean
		taxRateUuid: string
		taxRateName: string
	} & (
		| {
				variablePrice: true
		  }
		| {
				variablePrice: false
				amount: CommonTypes.Amount
				taxBreakdown: CommonTypes.TaxAmount
		  }
	)
	unitOfMeasure?: {
		uuid: string
		code: string
		name?: string
		isBase: boolean
	}
}
export type UnitOfMeasure = {
	uuid: string
	code: string
	name?: string
	conversionFactor: number
	conversionFactorOverride?: number
	isBase: boolean
	barcodes: string[]
	prices: Price[]
}
export type ProductVariant = {
	uuid: string
	barcodes: string[]
	sku?: string
	pricingMethod: CommonTypes.PricingMethodType
	trackInventory?: boolean
	prices: Price[]
	unitsOfMeasure?: UnitOfMeasure[]
}
export type Product = {
	uuid: string
	externalId?: string
	thirdPartySource?: ThirdPartySourceType
	title: string
	subtitle?: string
	description?: string
	imageUrl?: string
	status: ProductStatusType
	variants: ProductVariant[]
	salesChannels: {
		name: string
		uuid: string
	}[]
	groups: {
		uuid: string
		name: string
	}[]
	categories: {
		uuid: string
		name: string
	}[]
	images: ProductImage[]
	unitsOfMeasureEditable: boolean
}
export type PointOfSaleProductGroup = {
	uuid: string
	name: string
	icon?: string
	isDefault?: boolean
	image?: CommonTypes.Asset
}
export type AvailableProductListItem = {
	variantUuid: string
	title: string
	pricingMethod: CommonTypes.PricingMethodType
	unitPrice: CommonTypes.Amount
	currencyCode: string
	imageUrl?: string
	image?: CommonTypes.Asset
	primaryImage?: ProductImage
}
export type ProductMenuItem =
	| ({
			type: 'GROUP'
	  } & PointOfSaleProductGroup)
	| ({
			type: 'PRODUCT'
	  } & AvailableProductListItem)
export type ProductGroupListItem = {
	uuid: string
	name: string
	productCount: number
	feeCount: number
	image?: CommonTypes.Asset
}
export type Fee = {
	uuid: string
	name: string
	prices: Price[]
}
export type ProductGroup = {
	uuid: string
	name: string
	fees?: Fee[]
	image?: CommonTypes.Asset
}
export type PriceInput = {
	salesChannelUuid?: string
	salesChannelGroupUuid?: string
	currencyCode: string
	unitOfMeasureUuid?: string
	priceInfo: {
		taxInclusive: boolean
		taxRateUuid: string
	} & (
		| {
				variablePrice: true
		  }
		| {
				variablePrice: false
				amount: CommonTypes.Amount
		  }
	)
}
export type ProductCategory = {
	uuid: string
	name: string
	parentCategoryUuid?: string
	children?: ProductCategory[]
	productCount: number
	subtreeProductCount?: number
	image?: CommonTypes.Asset
}
export type UnitOfMeasureInput = {
	unitOfMeasureUuid: string
	conversionFactorOverride?: number
}
export type ProductOrganization = {
	groups: ProductGroup[]
	categories: ProductCategory[]
	fees: Fee[]
}
export type CatalogUnitOfMeasure = {
	uuid: string
	code: string
	name?: string
	conversionFactor?: number
	isDefaultEach: boolean
	isDefaultWeight: boolean
	managedBySync: boolean
	inUse: boolean
}
export type CatalogUnitOfMeasureInput = {
	code: string
	name?: string
	conversionFactor?: number
	isDefaultEach?: boolean
	isDefaultWeight?: boolean
}
export const ImportConflictMode = {
	Strict: 'strict',
	Reconcile: 'reconcile'
} as const
export type ImportConflictModeType = (typeof ImportConflictMode)[keyof typeof ImportConflictMode]
export type ValidateProductImportError = {
	message: string
	row: number
	column: string
}
export type ValidateProductImportResponse = {
	toCreate: number
	toUpdate: number
	errored: number
	errors: ValidateProductImportError[]
}

export const zProductStatus = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED'])
export const zProductImage = z.object({
	uuid: z.string(),
	position: z.number(),
	isPrimary: z.boolean(),
	variantUuid: z.string().optional(),
	asset: CommonTypes.zAsset
})
export const zProductListItem = z.object({
	uuid: z.string(),
	title: z.string(),
	imageUrl: z.string().optional(),
	salesChannels: z.array(z.string()),
	salesChannelGroups: z.array(z.string()),
	pricingMethod: z.array(CommonTypes.zPricingMethod),
	variants: z.array(
		z.object({
			uuid: z.string(),
			pricingMethod: CommonTypes.zPricingMethod
		})
	),
	status: zProductStatus,
	primaryImage: zProductImage.optional().nullable()
})
export const zThirdPartySource = z.enum(['STRUCT', 'LS_RETAIL', 'BUSINESS_CENTRAL'])
export const zPrice = z.object({
	salesChannel: z
		.object({
			name: z.string(),
			uuid: z.string()
		})
		.optional(),
	salesChannelGroup: z
		.object({
			name: z.string(),
			uuid: z.string()
		})
		.optional(),
	currencyCode: z.string(),
	priceInfo: z.intersection(
		z.object({
			taxInclusive: z.boolean(),
			taxRateUuid: z.string(),
			taxRateName: z.string()
		}),
		z.union([
			z.object({
				variablePrice: z.literal(true)
			}),
			z.object({
				variablePrice: z.literal(false),
				amount: CommonTypes.zAmount,
				taxBreakdown: CommonTypes.zTaxAmount
			})
		])
	),
	unitOfMeasure: z
		.object({
			uuid: z.string(),
			code: z.string(),
			name: z.string().optional(),
			isBase: z.boolean()
		})
		.optional()
})
export const zUnitOfMeasure = z.object({
	uuid: z.string(),
	code: z.string(),
	name: z.string().optional(),
	conversionFactor: z.number(),
	conversionFactorOverride: z.number().optional(),
	isBase: z.boolean(),
	barcodes: z.array(z.string()),
	prices: z.array(zPrice)
})
export const zProductVariant = z.object({
	uuid: z.string(),
	barcodes: z.array(z.string()),
	sku: z.string().optional(),
	pricingMethod: CommonTypes.zPricingMethod,
	trackInventory: z.boolean().optional(),
	prices: z.array(zPrice),
	unitsOfMeasure: z.array(zUnitOfMeasure).optional()
})
export const zProduct = z.object({
	uuid: z.string(),
	externalId: z.string().optional(),
	thirdPartySource: zThirdPartySource.optional(),
	title: z.string(),
	subtitle: z.string().optional(),
	description: z.string().optional(),
	imageUrl: z.string().optional(),
	status: zProductStatus,
	variants: z.array(zProductVariant),
	salesChannels: z.array(
		z.object({
			name: z.string(),
			uuid: z.string()
		})
	),
	groups: z.array(
		z.object({
			uuid: z.string(),
			name: z.string()
		})
	),
	categories: z.array(
		z.object({
			uuid: z.string(),
			name: z.string()
		})
	),
	images: z.array(zProductImage),
	unitsOfMeasureEditable: z.boolean()
})
export const zPointOfSaleProductGroup = z.object({
	uuid: z.string(),
	name: z.string(),
	icon: z.string().optional(),
	isDefault: z.boolean().optional(),
	image: CommonTypes.zAsset.optional().nullable()
})
export const zAvailableProductListItem = z.object({
	variantUuid: z.string(),
	title: z.string(),
	pricingMethod: CommonTypes.zPricingMethod,
	unitPrice: CommonTypes.zAmount,
	currencyCode: z.string(),
	imageUrl: z.string().optional(),
	image: CommonTypes.zAsset.optional().nullable(),
	primaryImage: zProductImage.optional().nullable()
})
export const zProductMenuItem = z.union([
	z.intersection(
		z.object({
			type: z.enum(['GROUP'])
		}),
		zPointOfSaleProductGroup
	),
	z.intersection(
		z.object({
			type: z.enum(['PRODUCT'])
		}),
		zAvailableProductListItem
	)
])
export const zProductGroupListItem = z.object({
	uuid: z.string(),
	name: z.string(),
	productCount: z.number(),
	feeCount: z.number(),
	image: CommonTypes.zAsset.optional().nullable()
})
export const zFee = z.object({
	uuid: z.string(),
	name: z.string(),
	prices: z.array(zPrice)
})
export const zProductGroup = z.object({
	uuid: z.string(),
	name: z.string(),
	fees: z.array(zFee).optional(),
	image: CommonTypes.zAsset.optional().nullable()
})
export const zPriceInput = z.object({
	salesChannelUuid: z.string().optional(),
	salesChannelGroupUuid: z.string().optional(),
	currencyCode: z.string(),
	unitOfMeasureUuid: z.string().optional(),
	priceInfo: z.intersection(
		z.object({
			taxInclusive: z.boolean(),
			taxRateUuid: z.string()
		}),
		z.union([
			z.object({
				variablePrice: z.literal(true)
			}),
			z.object({
				variablePrice: z.literal(false),
				amount: CommonTypes.zAmount
			})
		])
	)
})
export const zProductCategory = z.lazy(() =>
	z.object({
		uuid: z.string(),
		name: z.string(),
		parentCategoryUuid: z.string().optional(),
		children: z.array(zProductCategory).optional(),
		productCount: z.number(),
		subtreeProductCount: z.number().optional(),
		image: CommonTypes.zAsset.optional().nullable()
	})
)
export const zUnitOfMeasureInput = z.object({
	unitOfMeasureUuid: z.string().min(1),
	conversionFactorOverride: z.number().optional()
})
export const zProductOrganization = z.object({
	groups: z.array(zProductGroup),
	categories: z.array(zProductCategory),
	fees: z.array(zFee)
})
export const zCatalogUnitOfMeasure = z.object({
	uuid: z.string(),
	code: z.string(),
	name: z.string().optional(),
	conversionFactor: z.number().optional(),
	isDefaultEach: z.boolean(),
	isDefaultWeight: z.boolean(),
	managedBySync: z.boolean(),
	inUse: z.boolean()
})
export const zCatalogUnitOfMeasureInput = z.object({
	code: z.string().min(1),
	name: z.string().optional(),
	conversionFactor: z.number().optional(),
	isDefaultEach: z.boolean().optional(),
	isDefaultWeight: z.boolean().optional()
})
export const zImportConflictMode = z.enum(['strict', 'reconcile'])
export const zValidateProductImportError = z.object({
	message: z.string(),
	row: z.number(),
	column: z.string()
})
export const zValidateProductImportResponse = z.object({
	toCreate: z.number(),
	toUpdate: z.number(),
	errored: z.number(),
	errors: z.array(zValidateProductImportError)
})
